import { Routes, Route, Link, NavLink, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth.jsx";
import Landing from "./pages/Landing.jsx";
import Overview from "./pages/Overview.jsx";
import VerificationSettings from "./pages/VerificationSettings.jsx";
import SecuritySettings from "./pages/SecuritySettings.jsx";
import Whitelist from "./pages/Whitelist.jsx";
import Incidents from "./pages/Incidents.jsx";
import AuditLogs from "./pages/AuditLogs.jsx";
import VerificationHistory from "./pages/VerificationHistory.jsx";
import LoginError from "./pages/LoginError.jsx";
import { TermsOfService, PrivacyPolicy } from "./pages/Legal.jsx";
import DocsLayout from "./docs/DocsLayout.jsx";
import NotFound from "./pages/NotFound.jsx";
import { BRAND } from "@blackthorn/shared";
import SEO from "./components/SEO.jsx";

const dashNav = [
  { to: "/dashboard", label: "Overview", end: true },
  { to: "/dashboard/verification", label: "Verification", end: false },
  { to: "/dashboard/security", label: "Security", end: false },
  { to: "/dashboard/whitelist", label: "Whitelist", end: false },
  { to: "/dashboard/history", label: "Verification history", end: false },
  { to: "/dashboard/incidents", label: "Incidents", end: false },
  { to: "/dashboard/audit", label: "Audit logs", end: false },
];

function Shell({ children }: { children: React.ReactNode }) {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-bt-border bg-bt-surface">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-3">
              <img src="/blackthorn-icon.svg" alt="Blackthorn icon" className="w-8 h-8" />
              <img src="/blackthorn-logo.svg" alt="Blackthorn" className="hidden sm:block h-8" />
            </Link>
            <span className="hidden sm:block text-xs uppercase tracking-widest text-bt-muted">Dashboard</span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Link to="/docs" className="text-bt-muted hover:text-bt-text">Docs</Link>
            {me && <span className="text-bt-muted hidden sm:block">{me.username}</span>}
            <button
              className="border border-bt-border rounded px-3 py-1.5 hover:border-bt-steel"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
            >
              Log out
            </button>
          </div>
        </div>
      </header>
      <div className="flex-1 w-full max-w-6xl mx-auto px-4 flex gap-8 py-8">
        {/* Sidebar - simple, always visible */}
        <aside className="hidden md:block w-44 shrink-0">
          <nav className="sticky top-8 space-y-1 text-sm">
            {dashNav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `block rounded px-3 py-2 ${isActive ? "bg-bt-surface text-bt-text border border-bt-border" : "text-bt-muted hover:text-bt-text"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <main className="flex-1 min-w-0">{children}</main>
      </div>
      {/* Mobile nav fallback */}
      <div className="md:hidden border-t border-bt-border px-4 py-3 flex gap-4 overflow-x-auto text-sm text-bt-muted">
        {dashNav.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => (isActive ? "text-bt-text whitespace-nowrap" : "whitespace-nowrap hover:text-bt-text")}>
            {n.label}
          </NavLink>
        ))}
      </div>
      <footer className="border-t border-bt-border py-6 text-center text-xs text-bt-muted">{BRAND.name} - {BRAND.tagline}</footer>
    </div>
  );
}

function Dashboard() {
  const pages = [
    { path: "/dashboard", comp: <Overview />, title: "Dashboard Overview", description: "Your Blackthorn server at a glance: verification counts, recent incidents, and configuration state." },
    { path: "/dashboard/verification", comp: <VerificationSettings />, title: "Verification Settings", description: "Configure web verification, verified roles, alt-score thresholds, and token behavior. VPN blocking is coming soon." },
    { path: "/dashboard/security", comp: <SecuritySettings />, title: "Security Settings", description: "Anti-raid thresholds and actions, spam settings, audit-log monitoring targets, and dry-run switches." },
    { path: "/dashboard/whitelist", comp: <Whitelist />, title: "Whitelist", description: "Review pending whitelist requests, approve or remove entries, and set access policy." },
    { path: "/dashboard/history", comp: <VerificationHistory />, title: "Verification History", description: "Per-member verification history: screening outcomes, alt scores, and verified identities. Raw IPs are never stored." },
    { path: "/dashboard/incidents", comp: <Incidents />, title: "Incidents", description: "Browse every recorded raid and spam incident with its full timeline." },
    { path: "/dashboard/audit", comp: <AuditLogs />, title: "Audit Logs", description: "The full, filterable record of every action taken through the bot or dashboard." },
  ];
  return (
    <Shell>
      <Routes>
        {pages.map((p) => (
          <Route key={p.path} path={p.path} element={<><SEO title={p.title} description={p.description} path={p.path} />{p.comp}</>} />
        ))}
      </Routes>
    </Shell>
  );
}

/** Ambient animated background shared by every page - matches the banner's palette. */
function AmbientBackground() {
  return (
    <div aria-hidden className="bg-ambient">
      <div className="bg-ambient-glow" />
      <div className="bg-ambient-dots" />
      <div className="bg-ambient-drip" />
      <div className="bg-ambient-drip" style={{ animationDelay: "4.5s", left: "38%" }} />
      <div className="bg-ambient-drip" style={{ animationDelay: "7s", left: "62%" }} />
      <div className="bg-ambient-vignette" />
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AmbientBackground />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login/error" element={<LoginError />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/docs/*" element={<DocsLayout />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}
